<template>
  <b-modal id="happy-birthday-modal"
           size="xl"
           @show="onShow"
           @hide="onHide"
           :title="$t('birthdays')"
           centered>
    <b-tabs nav-class="sub-menu"
            content-class="pt-3"
            class="w-100">
      <b-tab v-for="(users, period) in birthdays"
             :active="period === 'today'"
             :key="`tab-${period}`"
             :title="$t(period)">
        <div class="data-table-wrapper" v-if="users.length > 0">
          <table class="table data-table more-button">
            <thead>
            <tr>
              <th style="width: 30%">{{ $t('name') }}</th>
              <th>{{ $t('age') }}</th>
              <th v-if="!rights.includes('hide_patient_phone')">{{ $t('phone') }}</th>
              <th v-if="!rights.includes('hide_patient_phone')">SMS</th>
              <th v-if="!rights.includes('hide_patient_phone')">Whatsapp</th>
              <th v-if="!rights.includes('hide_patient_phone')">Telegram</th>
              <th>E-mail</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(u, uind) in users" :key="`birthday-${uind}-${u.id}`">
              <td>{{ u.full_name }}</td>
              <td>{{ u.age }}</td>
              <td v-if="!rights.includes('hide_patient_phone')">{{ u.phone }}</td>
              <td v-if="!rights.includes('hide_patient_phone')">
                <CheckboxGroup class="mb-0 sm"
                               v-if="u.phone"
                               :fid="`sms-${period}-${u.id}`"
                               :checkboxvalue="u.id"
                               v-model="sendSms"/>
              </td>
              <td v-if="!rights.includes('hide_patient_phone')">
                <CheckboxGroup class="mb-0 sm"
                               v-if="u.phone"
                               :fid="`wa-${period}-${u.id}`"
                               :checkboxvalue="u.id"
                               v-model="sendWhatsapp"/>
              </td>
              <td v-if="!rights.includes('hide_patient_phone')">
                <CheckboxGroup class="mb-0 sm"
                               v-if="u.phone"
                               :fid="`telega-${period}-${u.id}`"
                               :checkboxvalue="u.id"
                               v-model="sendTelegram"/>
              </td>
              <td>
                <CheckboxGroup class="mb-0 sm"
                               v-if="u.email"
                               :fid="`email-${period}-${u.id}`"
                               :checkboxvalue="u.id"
                               v-model="sendEmails"/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="mb-4 alert alert-warning">
          {{ $t('no_birthdays_on_this_date') }}
        </div>
      </b-tab>
    </b-tabs>
    <OfficialWhatsappTemplateSelect v-if="template && sendWhatsapp.length"
                                    class="mb-3"
                                    :object="template"
                                    :templates="officialWhatsappTemplates"
                                    :copy-text="false"/>
    <div v-if="template">
      <b-button class="btn-collapse"
                v-spaceduck-collapse="`collapse-birth-templates`">
        {{ $t('text') }}
      </b-button>
      <spaceduck-collapse :id="`collapse-birth-templates`">
        <InputGroup v-model="template.email_theme"
                    class="mt-3"
                    :label="$t('email_topic')"/>
        <AutosizeTextArea v-model="template.email_text_stripped"
                          class="mt-3"
                          :label="$t('email_text')"/>
        <AutosizeTextArea v-model="template.sms"
                          class="mt-3"
                          :label="$t('sms_text')"/>
      </spaceduck-collapse>
    </div>


    <template #modal-footer="{ close }">
      <div class="modal-footer-buttons">
        <b-button class="ml-auto cancel d-inline-block align-middle" variant="themed" @click="close()">{{ $t('close') }}</b-button>
        <button :disabled="loading || (sendSms.length === 0 && sendEmails.length === 0 && sendWhatsapp.length === 0 && sendTelegram.length === 0)"
                @click="send"
                class="ml-3 btn-themed align-middle d-inline-block btn-themed-squared">
          {{ $t('send') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import { PatientService } from "@/services/api/patient.api.service"
import CheckboxGroup from "@/components/form/CheckboxGroup"
import InputGroup from "@/components/form/InputGroup"
import AutosizeTextArea from "@/components/form/AutosizeTextArea"
import SpaceduckCollapse from "@/components/parts/general/SpaceduckCollapse.vue"
import OfficialWhatsappTemplateSelect from "@/components/parts/datatable/form/settings/OfficialWhatsappTemplateSelect.vue"
import i18n from "@/lang/i18n"
import { computed, getCurrentInstance, ref } from "vue"
import { useStore } from "vuex"

const store = useStore()
const instance = getCurrentInstance()
const noty = instance?.appContext?.app?.noty
const bvModal = instance?.proxy?.$bvModal

const birthdays = ref([])
const template = ref(null) // the greeting texts (email, sms) and the official whatsapp template
// approved birthday templates of official whatsapp, empty when it is not connected
const officialWhatsappTemplates = ref([])
const loaded = ref(false)
// the patient ids to greet per channel
const sendSms = ref([])
const sendWhatsapp = ref([])
const sendTelegram = ref([])
const sendEmails = ref([])

const loading = computed(() => store.state.dom.loading)
const rights = computed(() => store.state.auth.rights)

const load = () => {
  PatientService.getBirthdays().then(res => {
    birthdays.value = res.data.birthdays
    template.value = res.data.template
    officialWhatsappTemplates.value = res.data.officialWhatsappTemplates || []
    loaded.value = true
  })
}

const send = () => {
  PatientService.sendBirthdayGreetings({
    sms: sendSms.value,
    email: sendEmails.value,
    whatsapp: sendWhatsapp.value,
    telegram: sendTelegram.value,
    email_topic: template.value.email_theme,
    email_text: template.value.email_text_stripped,
    sms_text: template.value.sms,
    official_whatsapp_template_id: template.value.official_whatsapp_template_id || null,
  }).then(() => {
    noty.info(i18n.global.t('success_message'))
    bvModal.hide('happy-birthday-modal')
  })
}

const onShow = () => {
  load()
}

const onHide = () => {
  loaded.value = false
  sendEmails.value = []
  sendSms.value = []
  sendWhatsapp.value = []
  sendTelegram.value = []
  birthdays.value = []
}
</script>
